import { Circle2d } from '../math/circle2d';
import { IPoint2d } from '../math/IPoint2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { Ray2d } from '../math/ray2d';
import { intersectRayAndCircle } from '../math/shape/intesection';
import { opposite } from '../math/vector';
import { IPureEdge } from '../types';

/** 圆弧的几何信息。轮廓点、边、墙体基础数据等都以可选字段的形式携带这两项 */
export interface IArcInfo {
  /** 圆心 */
  center: IPoint2d;
  /** 弧的方向 */
  clockwise: boolean;
}

/**
 * 判断一段数据（轮廓点、边、墙体基础数据等）描述的是圆弧而非直线段。
 *
 * 收窄后 `center` 与 `clockwise` 均为必选，可直接传给 `ArcSeg2d`，无需 `!` 断言。
 * `clockwise` 的合法取值包含 `false`，因此这里按 `undefined` 判断，禁止改为真值判断。
 */
export function isArc<T extends Partial<IArcInfo>>(target: T): target is T & IArcInfo {
  return target.center !== undefined && target.clockwise !== undefined;
}

// 进行径向裁剪
// return [外圆弧, 右直线段, 内圆弧, 左线段] 顺时针方向
export function getArcEdgesByRectBox(params: {
  rectCenter: IPoint2d;
  rectWidth: number;
  arcCenter: IPoint2d;
  thickness: number;
}): undefined | [IPureEdge, IPureEdge, IPureEdge, IPureEdge] {
  const { rectCenter, rectWidth, thickness, arcCenter } = params;

  const radius = Point2dUtils.distanceTo(arcCenter, rectCenter);
  const outerCircle = new Circle2d({ center: arcCenter, radius: radius + thickness / 2 });
  const innerCircle = new Circle2d({ center: arcCenter, radius: radius - thickness / 2 });

  const rectRadialDirection = Point2dUtils.getUnitDirection(arcCenter, rectCenter);
  const rectTangentialDirection = Point2dUtils.rotateDirection(rectRadialDirection, -Math.PI / 2);

  // 构造矩形框的左右侧射线
  const leftOrigin = Point2dUtils.addDistance(
    Point2dUtils.addDistance(rectCenter, thickness, rectRadialDirection),
    -rectWidth / 2,
    rectTangentialDirection
  );
  const leftRay = new Ray2d(leftOrigin, opposite(rectRadialDirection));

  const rightOrigin = Point2dUtils.addDistance(
    Point2dUtils.addDistance(rectCenter, thickness, rectRadialDirection),
    rectWidth / 2,
    rectTangentialDirection
  );
  const rightRay = new Ray2d(rightOrigin, opposite(rectRadialDirection));

  let intersectPoints = intersectRayAndCircle(leftRay, outerCircle);
  if (!intersectPoints) return;
  const leftOuter = intersectPoints[0];

  intersectPoints = intersectRayAndCircle(rightRay, outerCircle);
  if (!intersectPoints) return;
  const rightOuter = intersectPoints[0];

  intersectPoints = intersectRayAndCircle(leftRay, innerCircle);
  if (!intersectPoints) return;
  const leftInner = intersectPoints[0];

  intersectPoints = intersectRayAndCircle(rightRay, innerCircle);
  if (!intersectPoints) return;
  const rightInner = intersectPoints[0];

  return [
    {
      start: leftOuter,
      end: rightOuter,
      center: arcCenter,
      clockwise: true
    },
    {
      start: rightOuter,
      end: rightInner
    },
    {
      start: rightInner,
      end: leftInner,
      center: arcCenter,
      clockwise: false
    },
    {
      start: leftInner,
      end: leftOuter
    }
  ];
}

// 进行径向裁剪
export function getArcEdgeByRectBox(params: {
  rectCenter: IPoint2d;
  rectWidth: number;
  circle: Circle2d;
}): undefined | (Omit<IPureEdge, 'center' | 'clockwise'> & { center: IPoint2d; clockwise: boolean }) {
  const { rectCenter, rectWidth, circle } = params;

  const radius = circle.radius;
  const arcCenter = circle.center;

  const rectRadialDirection = Point2dUtils.getUnitDirection(arcCenter, rectCenter);
  const rectTangentialDirection = Point2dUtils.rotateDirection(rectRadialDirection, -Math.PI / 2);

  // 构造矩形框的左右侧射线
  const leftOrigin = Point2dUtils.addDistance(
    Point2dUtils.addDistance(arcCenter, radius * 2, rectRadialDirection),
    -rectWidth / 2,
    rectTangentialDirection
  );
  const leftRay = new Ray2d(leftOrigin, opposite(rectRadialDirection));

  const rightOrigin = Point2dUtils.addDistance(
    Point2dUtils.addDistance(arcCenter, radius * 2, rectRadialDirection),
    rectWidth / 2,
    rectTangentialDirection
  );
  const rightRay = new Ray2d(rightOrigin, opposite(rectRadialDirection));

  let intersectPoints = intersectRayAndCircle(leftRay, circle);
  if (!intersectPoints) return;
  const leftPoint = intersectPoints[0];

  intersectPoints = intersectRayAndCircle(rightRay, circle);
  if (!intersectPoints) return;
  const rightPoint = intersectPoints[0];

  return { start: leftPoint, end: rightPoint, center: circle.center, clockwise: true };
}
